/**
 * Draft apply request + atomic verification + applied-resource extraction.
 * Ported from useAgentChat.acceptDraft, retargeted at the vercel-backend
 * `/api/ai/apply-draft` endpoint (response shape: ApplyDraftResult).
 */

import { requestAiDraftEditorFlush } from '../aiDraftApplyEvents';

import { APPLY_DRAFT_ENDPOINT, authHeaders, getAccessToken } from './backend';

import type { AgentContext, ApplyDraftResult, AiDraft } from '@/shared/types/chat';

const UUID_VALUE_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class AuthExpiredError extends Error {
  constructor() {
    super('You must be logged in to apply changes.');
    this.name = 'AuthExpiredError';
  }
}

export interface ApplyDraftSuccess {
  appliedCount: number;
  resourceIds: string[];
}

function formatIssuePath(path: unknown): string {
  if (!Array.isArray(path) || path.length === 0) return '';
  return path.reduce((out: string, segment) => {
    if (typeof segment === 'number') return `${out}[${segment}]`;
    if (typeof segment !== 'string' || segment.length === 0) return out;
    return out.length > 0 ? `${out}.${segment}` : segment;
  }, '');
}

function summarizeApplyDraftErrorDetails(details: unknown): string {
  if (!details || typeof details !== 'object') return '';
  const record = details as Record<string, unknown>;
  const issue =
    Array.isArray(record.issues) && record.issues.length > 0
      ? (record.issues[0] as Record<string, unknown>)
      : null;
  const path = formatIssuePath(issue?.path);
  const message =
    typeof issue?.message === 'string' && issue.message.trim().length > 0
      ? issue.message.trim()
      : '';
  const slideOpPrefix =
    typeof record.opIndex === 'number'
      ? ` Slide op ${record.opIndex + 1}${typeof record.opType === 'string' ? ` (${record.opType})` : ''}.`
      : '';

  if (path && message)
    return `${slideOpPrefix} ${path}: ${message}.`.replace(/\s+/g, ' ').trimStart();
  if (message) return `${slideOpPrefix} ${message}.`.replace(/\s+/g, ' ').trimStart();
  if (typeof record.reason === 'string' && record.reason.trim().length > 0) {
    const receivedType =
      typeof record.receivedType === 'string' && record.receivedType.trim().length > 0
        ? ` Received ${record.receivedType}.`
        : '';
    return `${slideOpPrefix} ${record.reason}.${receivedType}`.replace(/\s+/g, ' ').trimStart();
  }
  if (slideOpPrefix) return slideOpPrefix;
  return '';
}

function buildApplyDraftErrorMessage(
  payload: {
    error?: string;
    code?: string;
    details?: unknown;
    requestId?: string;
  } | null
): string {
  const errorPrefix =
    typeof payload?.code === 'string' && payload.code.trim().length > 0 ? `${payload.code}: ` : '';
  const baseMessage = `${errorPrefix}${payload?.error ?? 'Draft apply failed'}`;
  const detailsMessage = summarizeApplyDraftErrorDetails(payload?.details);
  const detailsSuffix = detailsMessage ? ` ${detailsMessage}` : '';
  const requestIdMessage =
    typeof payload?.requestId === 'string' && payload.requestId.trim().length > 0
      ? ` Request ID: ${payload.requestId}.`
      : '';
  return `${baseMessage}${detailsSuffix}${requestIdMessage}`.trim();
}

function getAppliedResourceIds(draft: AiDraft, result: ApplyDraftResult): string[] {
  const resourceIds = new Set<string>();
  for (const item of result.results) {
    if (item.applied && item.entity === 'resource' && typeof item.id === 'string') {
      resourceIds.add(item.id);
    }
  }
  for (const change of draft.changes) {
    const payload = change.payload;
    if (!payload || typeof payload !== 'object') continue;
    if ((payload as Record<string, unknown>).entity !== 'resource') continue;
    const args = (payload as Record<string, unknown>).args;
    if (!args || typeof args !== 'object') continue;
    const resourceId = (args as Record<string, unknown>).resourceId;
    if (typeof resourceId === 'string' && UUID_VALUE_REGEX.test(resourceId)) {
      resourceIds.add(resourceId);
    }
  }
  return Array.from(resourceIds);
}

/**
 * Flushes the open editor (for document/slide focus), POSTs the draft, and
 * verifies the apply was atomic. Throws an Error with a user-facing message on
 * any failure; throws AuthExpiredError when there is no session.
 */
export async function applyDraftRequest(
  context: AgentContext,
  draft: AiDraft
): Promise<ApplyDraftSuccess> {
  const token = await getAccessToken();
  if (!token) {
    throw new AuthExpiredError();
  }

  const focusedResourceId = context.focus?.resourceId;
  const focusedResourceKind = context.focus?.resourceKind;
  if (
    focusedResourceId &&
    (focusedResourceKind === 'document' || focusedResourceKind === 'slide')
  ) {
    const editorFlushed = await requestAiDraftEditorFlush({
      draftId: draft.draftId,
      resourceId: focusedResourceId,
      resourceKind: focusedResourceKind,
    });
    if (!editorFlushed) {
      throw new Error(
        'Unable to save the open editor before applying the draft. Keep this tab open and try again.'
      );
    }
  }

  const response = await fetch(APPLY_DRAFT_ENDPOINT, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ context, draft }),
  });

  const json = (await response.json().catch(() => null)) as
    | (Partial<ApplyDraftResult> & {
        error?: string;
        code?: string;
        details?: unknown;
        requestId?: string;
      })
    | null;

  if (!response.ok || !json || !Array.isArray(json.results)) {
    throw new Error(buildApplyDraftErrorMessage(json));
  }

  const appliedCount = typeof json.appliedCount === 'number' ? json.appliedCount : 0;
  const expectedCount = draft.changes.length;
  const allApplied = json.results.every((r) => r?.applied === true);
  if (appliedCount !== expectedCount || !allApplied) {
    throw new Error('Draft apply did not complete atomically. No changes were committed.');
  }

  return {
    appliedCount,
    resourceIds: getAppliedResourceIds(draft, json as ApplyDraftResult),
  };
}
